import { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as fbSignOut,
  GoogleAuthProvider,
  signInWithRedirect,
  getRedirectResult,
} from "firebase/auth";
import { onSnapshot, doc, disableNetwork, enableNetwork, terminate, clearIndexedDbPersistence } from "firebase/firestore";
import { auth, db } from "../lib/firebase";
import {
  createUserProfile,
  getUserProfile,
  linkClientByEmail,
  generateCoachCode,
  createPendingClient,
  getCoachByInviteCode,
} from "../lib/firestore";
import { resolveOrgCurrency } from "../lib/currency";
import {
  assertAuthThrottle,
  recordAuthFailure,
  clearAuthThrottle,
} from "../lib/loginThrottle";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [needsOAuthSetup, setNeedsOAuthSetup] = useState(false);
  const [oauthName, setOauthName] = useState("");
  const [orgCurrency, setOrgCurrency] = useState(null);

  useEffect(() => {
    // Handle post-redirect Google sign-in (runs once on app load after Google redirect)
    getRedirectResult(auth)
      .then(async (result) => {
        if (result?.user) {
          const p = await getUserProfile(result.user.uid);
          if (!p) {
            setOauthName(result.user.displayName || "");
            setNeedsOAuthSetup(true);
          }
        }
      })
      .catch(() => {}); // no pending redirect — ignore

    let profileUnsub = null;

    const unsubAuth = onAuthStateChanged(auth, async (u) => {
      // Cancel any previous per-user profile listener
      if (profileUnsub) { profileUnsub(); profileUnsub = null; }

      if (!u) {
        setUser(null);
        setProfile(null);
        setLoading(false);
        return;
      }

      setUser(u);

      // ── One-time setup (new accounts / backfills) ────────────────────
      try {
        let p = await getUserProfile(u.uid);

        if (!p) {
          const pendingRole = localStorage.getItem("pendingRole");
          const pendingName = localStorage.getItem("pendingName");
          if (pendingRole) {
            const base = {
              email: (u.email || "").toLowerCase(),
              name: pendingName || u.email?.split("@")[0] || "User",
              role: pendingRole,
              createdAt: Date.now(),
              ...(pendingRole === "coach" && {
                subscriptionStatus: "free",
                plan: "free",
                inviteCode: generateCoachCode(u.uid),
                coachSource: "independent",
                onboardingType: "independent",
              }),
            };
            await createUserProfile(u.uid, base);
            localStorage.removeItem("pendingRole");
            localStorage.removeItem("pendingName");
            if (pendingRole === "client") {
              try { await linkClientByEmail(u.uid, (u.email || "").toLowerCase()); } catch {}
            }
          }
        }

        // Backfill inviteCode for coaches who don't have one, or fix if wrong length
        if (p && p.role === "coach" && (!p.inviteCode || p.inviteCode.length !== 5)) {
          try {
            await createUserProfile(u.uid, { inviteCode: generateCoachCode(u.uid) });
          } catch {}
        }
      } catch (err) {
        // Non-fatal: setup (new-account create / backfills) can fail on a flaky
        // connection. Still attach the profile listener below — with the
        // persistent cache it serves the last-known profile and recovers when
        // the network returns, instead of stranding the user on the splash.
        console.log("Profile setup failed:", err.code || err.message);
      }

      // ── Real-time listener: any Firestore write (including admin) ────
      // immediately updates the profile in every open tab/device.
      profileUnsub = onSnapshot(
        doc(db, "users", u.uid),
        (snap) => {
          setProfile(snap.exists() ? { uid: u.uid, ...snap.data() } : null);
          setLoading(false);
        },
        (err) => {
          console.error("Profile listener error:", err.code);
          setLoading(false);
        }
      );
    });

    return () => {
      unsubAuth();
      if (profileUnsub) profileUnsub();
    };
  }, []);

  // Org (gym/studio) member-facing currency — live listener so a change the
  // admin makes in Plans & Pricing reflects immediately for this coach,
  // without needing to sign out/in. Independent coaches (no organizationId)
  // never subscribe, so their per-coach country-based currency is untouched.
  useEffect(() => {
    const orgId = profile?.organizationId;
    if (!orgId) { setOrgCurrency(null); return; }
    const unsub = onSnapshot(
      doc(db, "organizations", orgId, "settings", "pricing"),
      (snap) => setOrgCurrency(resolveOrgCurrency(snap.data()?.currencyCode)),
      () => setOrgCurrency(null)
    );
    return unsub;
  }, [profile?.organizationId]);

  async function signUp({ email, password, name, role, phone, country, inviteCode, coachId, coachingType }) {
    try { await enableNetwork(db); } catch {}
    // Throttle: max 5 sign-up attempts per email per 15 min
    assertAuthThrottle("signup", email);
    let cred;
    try {
      cred = await createUserWithEmailAndPassword(auth, email, password);
    } catch (err) {
      recordAuthFailure("signup", email);
      throw err;
    }
    clearAuthThrottle("signup", email);
    const emailLower = email.toLowerCase();

    // Resolve coachId from invite code NOW — the user is authenticated so
    // Firestore reads are permitted. This avoids the permission-denied error
    // that occurred when the lookup was attempted before account creation.
    let resolvedCoachId = coachId;
    if (role === "client" && inviteCode && !resolvedCoachId) {
      const coach = await getCoachByInviteCode(inviteCode.trim().toUpperCase());
      if (!coach) {
        // Remove the just-created auth account so the user can try again cleanly
        try { await cred.user.delete(); } catch {}
        const err = new Error("INVALID_INVITE_CODE");
        throw err;
      }
      resolvedCoachId = coach.uid;
    }

    const base = {
      email: emailLower,
      name,
      role,
      createdAt: Date.now(),
      ...(role === "coach" && {
        subscriptionStatus: "free",
        plan: "free",
        inviteCode: generateCoachCode(cred.user.uid),
        coachSource: "independent",
        onboardingType: "independent",
        country: country || "IN",
        phone: phone || "",
      }),
      ...(role === "client" && resolvedCoachId && { linkedCoachId: resolvedCoachId }),
    };
    await createUserProfile(cred.user.uid, base);

    if (role === "client") {
      // Try email linking first (coach may have already added them manually)
      try {
        await linkClientByEmail(cred.user.uid, emailLower);
      } catch {}

      // Create a pending request for the coach to accept
      if (resolvedCoachId) {
        try {
          await createPendingClient(resolvedCoachId, {
            name,
            email: emailLower,
            phone: phone || "",
            userId: cred.user.uid,
            coachingType: coachingType || "live",
          });
        } catch {}
      }
    }

    const p = await getUserProfile(cred.user.uid);
    setProfile(p);
    return p;
  }

  async function signIn({ email, password }) {
    try { await enableNetwork(db); } catch {}
    // Throttle: max 5 failed sign-in attempts per email per 15 min
    assertAuthThrottle("signin", email);
    let cred;
    try {
      cred = await signInWithEmailAndPassword(auth, email, password);
    } catch (err) {
      recordAuthFailure("signin", email);
      throw err;
    }
    clearAuthThrottle("signin", email);
    let p = await getUserProfile(cred.user.uid);
    if (!p) {
      const pendingRole = localStorage.getItem("pendingRole");
      const pendingName = localStorage.getItem("pendingName");
      if (pendingRole) {
        const base = {
          email: (cred.user.email || email).toLowerCase(),
          name: pendingName || email.split("@")[0],
          role: pendingRole,
          createdAt: Date.now(),
          ...(pendingRole === "coach" && {
            subscriptionStatus: "free",
            plan: "free",
            inviteCode: generateCoachCode(cred.user.uid),
            coachSource: "independent",
            onboardingType: "independent",
          }),
        };
        await createUserProfile(cred.user.uid, base);
        localStorage.removeItem("pendingRole");
        localStorage.removeItem("pendingName");
        if (pendingRole === "client") {
          try {
            await linkClientByEmail(cred.user.uid, (cred.user.email || email).toLowerCase());
          } catch {}
        }
        p = await getUserProfile(cred.user.uid);
      }
    }

    if (p && p.role === "client" && !p.clientId) {
      try {
        await linkClientByEmail(cred.user.uid, (cred.user.email || email).toLowerCase());
        p = await getUserProfile(cred.user.uid);
      } catch {}
    }
    setProfile(p);
    return p;
  }

  /** Google OAuth — uses redirect (works reliably on all mobile browsers) */
  async function signInWithGoogle() {
    try { await enableNetwork(db); } catch {}
    const provider = new GoogleAuthProvider();
    await signInWithRedirect(auth, provider);
    // Page navigates away to Google — result handled by getRedirectResult on return
  }

  /** Called after Google OAuth when the user has no profile yet */
  async function completeOAuthProfile({ name, role, phone, inviteCode, coachingType }) {
    const u = auth.currentUser;
    if (!u) throw new Error("No authenticated user");

    let resolvedCoachId = null;
    if (role === "client" && inviteCode) {
      const coach = await getCoachByInviteCode(inviteCode.trim().toUpperCase());
      if (!coach) throw new Error("INVALID_INVITE_CODE");
      resolvedCoachId = coach.uid;
    }

    const emailLower = (u.email || "").toLowerCase();
    const base = {
      email: emailLower,
      name: name || u.displayName || emailLower.split("@")[0],
      role,
      createdAt: Date.now(),
      ...(role === "coach" && {
        subscriptionStatus: "free",
        plan: "free",
        inviteCode: generateCoachCode(u.uid),
        coachSource: "independent",
        onboardingType: "independent",
      }),
      ...(role === "client" && resolvedCoachId && { linkedCoachId: resolvedCoachId }),
    };
    await createUserProfile(u.uid, base);

    if (role === "client") {
      try { await linkClientByEmail(u.uid, emailLower); } catch {}
      if (resolvedCoachId) {
        try {
          await createPendingClient(resolvedCoachId, {
            name: base.name,
            email: emailLower,
            phone: phone || "",
            userId: u.uid,
            coachingType: coachingType || "live",
          });
        } catch {}
      }
    }

    setNeedsOAuthSetup(false);
    const p = await getUserProfile(u.uid);
    setProfile(p);
    return p;
  }

  async function signOut() {
    setUser(null);
    setProfile(null);
    setLoading(false);
    setNeedsOAuthSetup(false);
    // Silence all open Firestore listeners before revoking the auth token.
    // Without this, the streaming channel fires a final request with an
    // invalidated session and produces a 400 Bad Request in the console.
    try { await disableNetwork(db); } catch {}
    await fbSignOut(auth);
    // Wipe the on-device Firestore cache so the next user on a shared device
    // can't see the previous user's data. Clearing requires a terminated
    // instance, so reload — the app lands back on the login screen anyway.
    try {
      await terminate(db);
      await clearIndexedDbPersistence(db);
    } catch {}
    window.location.reload();
  }

  async function refreshProfile() {
    if (!user) return null;
    const p = await getUserProfile(user.uid);
    setProfile(p);
    return p;
  }

  return (
    <AuthCtx.Provider value={{ user, profile, loading, signUp, signIn, signOut, refreshProfile, signInWithGoogle, completeOAuthProfile, needsOAuthSetup, oauthName, orgCurrency }}>
      {children}
    </AuthCtx.Provider>
  );
}

export function useAuth() {
  return useContext(AuthCtx);
}